import React, { useState, useEffect, useImperativeHandle, forwardRef } from 'react';
import { Pagination } from '../pagination/pagination';
import { SearchInput } from './SearchInput';
import './SearchTable.css';
import { Translates } from '@/services/repository/i18n';

export interface PageQuery {
  pageSize: number;
  pageNumber: number;
  search: string;
}

export interface PageResponse<T> {
  pageSize: number;
  pageNumber: number;
  totalPages: number;
  data: T[] | undefined;
}

export interface SearchTableParams<T> {
  fetch: (q: PageQuery) => Promise<PageResponse<T>>;
  toolbox?: () => React.ReactElement;
  header?: () => React.ReactElement;
  row: (row: T, state: PageResponse<T>, setState: React.Dispatch<React.SetStateAction<PageResponse<T> | undefined>>) => React.ReactElement;
  pageQuery: PageQuery;
  translates: Translates
  className?: string;
}

export interface SearchTableRef {
  refresh: () => void;
}

// Correctly typed forwardRef with generics
export const SearchTable = forwardRef<SearchTableRef, SearchTableParams<any>>(
  <T,>(
    { row, fetch, pageQuery, translates, toolbox, header, className }: SearchTableParams<T>,
    ref: React.Ref<SearchTableRef>
  ) => {
    const [resp, setResp] = useState<PageResponse<T>>();
    const [error, setError] = useState<string>();
    const respRef = React.useRef<PageResponse<T> | undefined>(resp);

    useEffect(() => {
      respRef.current = resp;
    }, [resp]);

    const update = (query: PageQuery) => {
      fetch(query).then((response) => setResp(response || {
        pageSize: 30,
        pageNumber: 1,
        totalPages: 0,
        data: []
      }), (msg) => {
        setError(msg)
      });
    };

    const handleSearchChange = React.useCallback(
      (search: string) => {
        if (!respRef.current) return;
        update({ pageNumber: 1, pageSize: respRef.current.pageSize, search });
      },
      []
    );

    useEffect(() => {
      update(pageQuery);
    }, [pageQuery]);

    useImperativeHandle(ref, () => ({
      refresh: () => update(pageQuery),
    }));

    if (error) return <div className='main-search-error'>{error}</div>

    if (!resp) return <div className='spinner'></div>

    return (
      <div className={className || 'main-search-table'}>
        <div className="toolbox">
          <div className="left">{toolbox && toolbox()}</div>
          <div className="right">
            <SearchInput onDebouncedChange={handleSearchChange} translates={translates} />
          </div>
        </div>
        <div className='search-body-wrap'>
          <div className="search-body">
            {header && <div className="row header">{header()}</div>}
            {resp.data?.map((item) => (
              <div className="row" key={(item as any).id}>
                {row(item, resp, setResp)}
              </div>
            ))}
          </div>
        </div>
        {!resp.data?.length && (
          <div className="empty-search-table-message">
            {translates.for('elements.search.empty_table')}
          </div>
        )}
        <Pagination
          translates={translates}
          pageQuery={resp}
          onChange={(pageQuery) => update({ ...pageQuery, search: '' })}
        />
      </div>
    );
  }
);
